import React, { useEffect, useState } from "react";
import { CardContainer } from "../../../components/Card/CardContainer";
import { useMessageContext } from "../../../context/MessageProvider";
import {
  Alert,
  Card,
  Checkbox,
  Col,
  ConfigProvider,
  DatePicker,
  Divider,
  Form,
  message,
  Modal,
  Popconfirm,
  Popover,
  Row,
  Table,
  Tabs,
  Tag,
  Typography,
} from "antd";
import Button from "../../../components/Button/Button";
import { useNavigate, useSearchParams } from "react-router-dom";
import {
  CalendarOutlined,
  DeleteOutlined,
  ExclamationCircleOutlined,
  FormOutlined,
  SearchOutlined,
  UnorderedListOutlined,
  VerticalAlignBottomOutlined,
} from "@ant-design/icons";
import Text from "../../../components/Text/Text";
import { color, icon, image } from "../../../resource";
import Input from "../../../components/Input/Input";
import Select from "../../../components/Select/Select";
import { FlexCol } from "../../../components/Container/Container";
import { FixedType } from "rc-table/lib/interface";
import { numberFormatter } from "../../../utility/Formatter";
import {
  deleteCoDiscoutListById,
  excelMutiCoList,
  getCoDiscountList,
  getCoDiscountSummary,
  getCoDiscoutType,
} from "../../../datasource/CODiscountDatasource";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import timezone from "dayjs/plugin/timezone";
import Permission, { checkPermission } from "../../../components/Permission/Permission";
import { ActionBtn } from "../../../components/Button/TableActionButton";
import { useEffectOnce } from "react-use";
import { zoneDatasource } from "../../../datasource/ZoneDatasource";
import { useTabState } from "../../../hook/useTabState";
import SortFieldCustom from "../../../components/SortFieldTable/SortFieldCustom";
import { useRecoilValue } from "recoil";
import { roleAtom } from "../../../store/RoleAtom";
import { useNotificationContext } from "../../../context/NotificationProvider";

dayjs.extend(utc);
dayjs.extend(timezone);

export const IndexCoDiscountList: React.FC = () => {
  const profile = JSON.parse(localStorage.getItem("profile")!);
  const roleData = useRecoilValue(roleAtom);
  const isExcel = checkPermission(["coDiscountList", "excel"], roleData);
  const { messageApi } = useMessageContext();
  const orderTabState = useTabState();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [tab, setTab] = useState(searchParams.get("tab") || "ALL");
  const [data, setData] = useState<any>([]);
  const [dataSummary, setDataSummary] = useState<any>([]);
  const [coSelected, setCoSelected] = useState<any[]>([]);
  const [modalMutiSelect, setModalMutiSelect] = useState<boolean>(false);
  const [deleteConfirmId, setDeleteConfirmId] = useState<string | null>(null);
  const [form] = Form.useForm();
  const startDateWatch = Form.useWatch("startDate", form);
  const endDateWatch = Form.useWatch("endDate", form);
  const [isExporting, setIsExporting] = useState(false);
  const { startUploadProcess } = useNotificationContext();
  const [progressValue, setProgressValue] = useState(0);

  const pageSize = 10;
  const [page, setPage] = useState<string>(searchParams.get("page") || "1");
  const [zoneList, setZoneList] = useState<any>([]);
  const [coType, setCoType] = useState([]);
  const [deleteModal, setDeleteModal] = useState<{ id: string; open: boolean }>({
    id: "",
    open: false,
  });

  const [searchZone, setSearchZone] = useState<any>(searchParams.get("searchZone") || undefined);
  const [searchText, setSearchText] = useState<any>(searchParams.get("searchText") || undefined);
  const [searchType, setSearchType] = useState<any>(searchParams.get("searchType") || undefined);
  const [searchDate, setSearchDate] = useState<any>([
    searchParams.get("startDate") || undefined,
    searchParams.get("endDate") || undefined,
  ]);

  const [loading, setLoading] = useState(false);

  const fetchData = async () => {
    await getCoDiscountList({
      company: profile.company,
      page,
      take: pageSize,
      status: tab === "ALL" ? undefined : tab,
      zone: searchZone,
      searchText,
      creditMemoTypeId: searchType,
      startDate: searchDate[0],
      endDate: searchDate[1],
      sortField: orderTabState.sortDirection ? orderTabState.sortField : "updatedAt",
      sortDirection: orderTabState.sortDirection,
    }).then((res) => {
      const mapData = res.data.map((d: any) => {
        const isChecked = coSelected.find((p) => p.coId === d.coId);
        return {
          ...d,
          isChecked: isChecked ? true : false,
        };
      });
      setData({ ...res, data: mapData });
    });
  };
  const fetchSummary = async () => {
    await getCoDiscountSummary({ company: profile.company }).then((res) => {
      setDataSummary(res);
    });
  };
  const fetchZone = async () => {
    await zoneDatasource.getAllZoneByCompany(profile.company).then((res) => {
      setZoneList(res);
    });
  };
  const fetchDataType = async () => {
    await getCoDiscoutType({
      company: profile?.company,
      page: 1,
      take: 999,
      status: true,
    }).then((res) => {
      setCoType(res.data);
    });
  };
  const setParams = (params?: {
    tab?: string;
    searchText?: string;
    searchType?: string;
    searchZone?: string;
    startDate?: string;
    endDate?: string;
    page?: string;
  }) => {
    const nextTab = params?.tab ?? tab;
    const nextSearchText = params?.searchText ?? searchText;
    const nextSearchType = params?.searchType ?? searchType;
    const nextSearchZone = params?.searchZone ?? searchZone;
    const nextStartDate = params?.startDate ?? searchDate[0];
    const nextEndDate = params?.endDate ?? searchDate[1];
    const nextPage = params?.page ?? page;

    setSearchParams({
      tab: nextTab || "ALL",
      searchText: nextSearchText || "",
      searchType: nextSearchType || "",
      searchZone: nextSearchZone || "",
      startDate: nextStartDate || "",
      endDate: nextEndDate || "",
      page: nextPage,
    });
  };

  useEffectOnce(() => {
    fetchSummary();
    fetchZone();
    fetchDataType();
  });

  useEffect(() => {
    setLoading(true);
    fetchData().finally(() => setLoading(false));
  }, [
    page,
    pageSize,
    tab,
    searchZone,
    searchText,
    searchType,
    searchDate,
    orderTabState.sortField,
    orderTabState.sortDirection,
  ]);

  const PageTitle = () => {
    return (
      <Row justify={"space-between"} align={"middle"} gutter={8}>
        <Col>
          <div>
            <span
              className='card-label font-weight-bolder text-dark'
              style={{ fontSize: 22, fontWeight: "bold" }}
            >
              รายการ เพิ่ม/ลด ส่วนลดดูแลราคา
            </span>
          </div>
        </Col>
        <Col>
          <Row justify={"end"} gutter={8}>
            {isExcel && (
              <Col>
                <Button
                  type='primary'
                  typeButton={coSelected.length ? "primary" : "disabled"}
                  title={
                    <>
                      <VerticalAlignBottomOutlined style={{ fontSize: "18px" }} /> Export (
                      {coSelected.length}/5)
                    </>
                  }
                  height={40}
                  onClick={() =>
                    coSelected.length && (setModalMutiSelect(true), form.resetFields())
                  }
                />
              </Col>
            )}
            <Col>
              <Button
                type='primary'
                title='+ สร้างส่วนลดดูแลราคา'
                height={40}
                onClick={() => navigate(`/newDiscountCo/coDiscountList/0/list`)}
              />
            </Col>
          </Row>
        </Col>
      </Row>
    );
  };
  const Dashboard = () => {
    const mapData = [
      {
        type: "ส่วนลดรวม",
        value: dataSummary?.totalDiscount ?? 0,
        color: "#0068F4",
        bgColor: "#EEF6FE",
      },
      {
        type: "ใช้ไป",
        value: dataSummary?.used > 0 ? `-${dataSummary?.used}` : `${dataSummary?.used}`,
        color: "#F24040",
        bgColor: "#FEECEC",
      },
      { type: "คงเหลือ", value: dataSummary?.remaining ?? 0, color: "#008236", bgColor: "#F0FDF4" },
      {
        type: "ใกล้หมดอายุ (รายการ)",
        value: dataSummary?.nearExpiryCount ?? 0,
        color: "#7A6B0F",
        bgColor: "#FFF7C9",
      },
    ];
    return (
      <>
        <Row justify={"space-between"} gutter={8}>
          {mapData.map((item, index) => (
            <Col span={6} key={index}>
              <Card
                style={{
                  backgroundColor: item.bgColor,
                  borderColor: item.bgColor,
                  borderRadius: 5,
                }}
              >
                <span
                  className='card-label font-weight-bolder'
                  style={{
                    fontSize: 30,
                    fontWeight: "bold",
                    color: item.color,
                    borderRadius: 5,
                  }}
                >
                  {item.type.includes("รายการ") ? item.value : numberFormatter(item.value)}
                </span>
                <br />
                <Text style={{ fontSize: 16 }}>{item.type}</Text>{" "}
                {index == 0 && (
                  <Popover
                    placement='bottom'
                    content={
                      <Text level={6} color='white'>
                        เฉพาะรายการที่เปิดใช้งาน
                      </Text>
                    }
                    color={color.BK}
                  >
                    <ExclamationCircleOutlined />
                  </Popover>
                )}
              </Card>
            </Col>
          ))}
        </Row>
      </>
    );
  };
  const selectedOne = (e: boolean, coId: string) => {
    const newData = data.data.map((item: any) =>
      item.coId === coId ? { ...item, isChecked: e } : item,
    );
    setData({ ...data, data: newData });
    setCoSelected((prev) => {
      if (!e) {
        return prev.filter((item) => item.coId !== coId);
      }
      const selectedItem = newData.find((item) => item.coId === coId);
      if (!selectedItem) {
        return prev;
      }

      return Array.from(new Map([...prev, selectedItem].map((item) => [item.coId, item])).values());
    });
  };
  const resetSelected = () => {
    const mapData = data.data.map((d: any) => {
      return {
        ...d,
        isChecked: false,
      };
    });
    setData({ ...data, data: mapData });
    setCoSelected([]);
  };
  const exportMutiExcel = async () => {
    try {
      await form.validateFields().then(async (values) => {
        setIsExporting(true);
        setModalMutiSelect(false);
        const { startDate, endDate } = form.getFieldsValue(true);
        const coIdList = coSelected.map((p) => p.coId);
        const startDateStr = dayjs(startDate).format("YYYY-MM-DD");
        const endDateStr = dayjs(endDate).format("YYYY-MM-DD");
        const params = {
          coIds: coIdList,
          startDate: startDateStr,
          endDate: endDateStr,
        };
        let blob: Blob = new Blob([], {
          type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
        });
        try {
          const config = await excelMutiCoList(params);
          if (config) {
            blob = new Blob([config], {
              type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
            });
          }
          startUploadProcess(
            "",
            progressValue,
            setProgressValue,
            {
              placement: "bottomRight",
              duration: 2,
              label: "กำลังเตรียมไฟล์ของคุณ รอสักครู่!",
              progressInterval: 900,
            },
            async () => {
              try {
                const a = document.createElement("a");
                a.href = URL.createObjectURL(blob);
                a.download = `co-list-${new Date()
                  .toLocaleDateString("us-EN", {
                    year: "numeric",
                    month: "2-digit",
                    day: "2-digit",
                  })
                  .replace(/\//g, "-")}.xlsx`;
                document.body.appendChild(a);
                a.click();
                document.body.removeChild(a);
                window.URL.revokeObjectURL(a.href);
                // formatFileSize(blob.size);
              } catch (error) {
                message.error("Export Excel ไม่สำเร็จ");
              } finally {
                setModalMutiSelect(false);
                setIsExporting(false);
              }
            },
          );
        } catch (error) {
          console.error("Error submitting report:", error);
        } finally {
          resetSelected();
          setModalMutiSelect(false);
        }
      });
    } catch (e: any) {
      console.log(e);
    }
  };

  const columns: any = [
    {
      title: "#",
      dataIndex: "index",
      key: "index",
      align: "center",
      hidden: !isExcel,
      render: (value: any, row: any, index: number) => {
        return {
          children: (
            <FlexCol align='center'>
              <Checkbox
                checked={row.isChecked}
                onChange={(e) => selectedOne(e.target.checked, row.coId)}
                disabled={coSelected.length === 5 && !row.isChecked}
              />
            </FlexCol>
          ),
        };
      },
    },
    {
      title: (
        <SortFieldCustom
          title='รหัสส่วนลด'
          sortDirection={
            orderTabState.sortField === "code" ? orderTabState.sortDirection : undefined
          }
          onSortChange={() => orderTabState.handleSortChange("code")}
        />
      ),
      dataIndex: "code",
      key: "code",
      render: (value: any, row: any, index: number) => {
        return {
          children: (
            <FlexCol>
              <Text>{value}</Text>
            </FlexCol>
          ),
        };
      },
    },
    {
      title: (
        <SortFieldCustom
          title='ชื่อรายการ'
          sortDirection={
            orderTabState.sortField === "name" ? orderTabState.sortDirection : undefined
          }
          onSortChange={() => orderTabState.handleSortChange("name")}
        />
      ),
      dataIndex: "name",
      key: "name",
      render: (value: any, row: any, index: number) => {
        return {
          children: (
            <FlexCol>
              <Text>{value}</Text>
              <Text level={6} color='Text3'>
                {row?.shopCount} ร้านค้า{" "}
                {row?.productCount > 0 ? `• ${row?.productCount} สินค้า` : ""}
              </Text>
            </FlexCol>
          ),
        };
      },
    },
    {
      title: (
        <SortFieldCustom
          title='ประเภทส่วนลด'
          sortDirection={
            orderTabState.sortField === "creditMemoTypeCode"
              ? orderTabState.sortDirection
              : undefined
          }
          onSortChange={() => orderTabState.handleSortChange("creditMemoTypeCode")}
        />
      ),
      dataIndex: "creditMemoTypeCode",
      key: "creditMemoTypeCode",
      render: (value: any, row: any, index: number) => {
        return {
          children: (
            <FlexCol>
              <Tag style={{ backgroundColor: `${row?.creditMemoType?.color}20`, border: "none" }}>
                <Typography.Text
                  style={{
                    color: row?.creditMemoType?.color,
                    fontFamily: "IBM Plex Sans Thai",
                    fontSize: 15,
                    fontWeight: "bold",
                  }}
                >
                  {row?.creditMemoType?.code} • {row?.creditMemoType?.nameTh}
                </Typography.Text>
              </Tag>
            </FlexCol>
          ),
        };
      },
    },
    {
      title: (
        <SortFieldCustom
          title='วิธีคิดส่วนลด'
          sortDirection={
            orderTabState.sortField === "type" ? orderTabState.sortDirection : undefined
          }
          onSortChange={() => orderTabState.handleSortChange("type")}
        />
      ),
      dataIndex: "type",
      key: "type",
      render: (value: any, row: any, index: number) => {
        return {
          children: (
            <FlexCol>
              <Text>{value === "SKU" ? "บาท (฿) / หน่วย" : "% ของยอดบิล"}</Text>
            </FlexCol>
          ),
        };
      },
    },
    {
      title: (
        <SortFieldCustom
          title='ระยะเวลา'
          sortDirection={
            orderTabState.sortField === "startDate" ? orderTabState.sortDirection : undefined
          }
          onSortChange={() => orderTabState.handleSortChange("startDate")}
        />
      ),
      dataIndex: "startDate",
      key: "startDate",
      width: "135px",
      render: (value: any, row: any, index: number) => {
        return {
          children: (
            <FlexCol>
              <Text>
                {dayjs(row?.startDate).format("DD/MM/YYYY")} -{" "}
                {dayjs(row?.endDate).format("DD/MM/YYYY")}
              </Text>
            </FlexCol>
          ),
        };
      },
    },
    {
      title: (
        <SortFieldCustom
          title='ส่วนลดรวม'
          sortDirection={
            orderTabState.sortField === "totalDiscount" ? orderTabState.sortDirection : undefined
          }
          onSortChange={() => orderTabState.handleSortChange("totalDiscount")}
        />
      ),
      dataIndex: "totalDiscount",
      key: "totalDiscount",
      render: (value: any, row: any, index: number) => {
        return {
          children: (
            <FlexCol style={{ alignItems: "flex-end" }}>
              <Text>{numberFormatter(value)} ฿</Text>
            </FlexCol>
          ),
        };
      },
    },
    {
      title: (
        <SortFieldCustom
          title='ใช้ไป'
          sortDirection={
            orderTabState.sortField === "used" ? orderTabState.sortDirection : undefined
          }
          onSortChange={() => orderTabState.handleSortChange("used")}
        />
      ),
      dataIndex: "used",
      key: "used",
      render: (value: any, row: any, index: number) => {
        return {
          children: (
            <FlexCol style={{ alignItems: "flex-end" }}>
              <Text color={value > 0 ? "error" : "Text1"}>
                {value > 0 ? `-${numberFormatter(value)}` : numberFormatter(value)} ฿
              </Text>
            </FlexCol>
          ),
        };
      },
    },
    {
      title: (
        <SortFieldCustom
          title='คงเหลือ'
          sortDirection={
            orderTabState.sortField === "remaining" ? orderTabState.sortDirection : undefined
          }
          onSortChange={() => orderTabState.handleSortChange("remaining")}
        />
      ),
      dataIndex: "remaining",
      key: "remaining",
      render: (value: any, row: any, index: number) => {
        return {
          children: (
            <FlexCol style={{ alignItems: "flex-end" }}>
              <Text color={value > 0 ? "EmeraldGreen" : "Text1"}>{numberFormatter(value)} ฿</Text>
            </FlexCol>
          ),
        };
      },
    },
    {
      title: (
        <SortFieldCustom
          title='แสดงให้ Dealer เห็น'
          sortDirection={
            orderTabState.sortField === "visibleToDealer" ? orderTabState.sortDirection : undefined
          }
          onSortChange={() => orderTabState.handleSortChange("visibleToDealer")}
        />
      ),
      dataIndex: "visibleToDealer",
      key: "visibleToDealer",
      render: (value: any, row: any, index: number) => {
        return {
          children: (
            <FlexCol style={{ alignItems: "center" }}>
              <Tag
                style={{
                  backgroundColor: value ? "#E2FDEB" : "#F3F6F9",
                  border: "none",
                }}
              >
                <Text color={value ? "EmeraldGreen" : "Disable"} level={6} fontWeight={600}>
                  {value ? "แสดง" : "ไ่ม่แสดง"}
                </Text>
              </Tag>
            </FlexCol>
          ),
        };
      },
    },
    {
      title: "สถานะ",
      dataIndex: "viewStatus",
      key: "viewStatus",
      fixed: "right" as FixedType | undefined,
      render: (value: any, row: any, index: number) => {
        const calDateStart = dayjs
          .utc(row?.startDate)
          .tz("Asia/Bangkok")
          .diff(dayjs().tz("Asia/Bangkok"), "minute");

        return {
          children: (
            <FlexCol>
              <Tag
                style={{
                  backgroundColor: mapStatus(value)?.bgColor,
                  border: "none",
                }}
              >
                <Text style={{ color: mapStatus(value)?.textColor }} level={6} fontWeight={600}>
                  {mapStatus(value)?.text}{" "}
                  {row.daysToExpiry <= 7 && value.includes("NEAR")
                    ? `(${row.daysToExpiry} วัน)`
                    : ""}
                  {calDateStart > 0 && value === "ACTIVE" ? `(รอเริ่ม)` : ""}
                </Text>
              </Tag>
            </FlexCol>
          ),
        };
      },
    },
    {
      title: "จัดการ",
      dataIndex: "action",
      key: "action",
      fixed: "right" as FixedType | undefined,
      render: (value: any, row: any, index: number) => {
        return {
          children: (
            <Row justify={"space-between"} gutter={8}>
              <Col>
                <Permission permission={["coDiscountList", "view"]}>
                  <ActionBtn
                    onClick={() =>
                      navigate("/newDiscountCo/detailCODiscountList/" + row.coId + "/coList")
                    }
                    icon={<UnorderedListOutlined />}
                  />
                </Permission>
              </Col>
              <Col>
                <Permission permission={["coDiscountList", "edit"]}>
                  <Popover
                    trigger={row.viewStatus === "EXPIRED" ? "click" : undefined}
                    placement='bottom'
                    content={
                      <Text level={6} color='white'>
                        ไม่สามารถแก้ไขรายการส่วนลดที่หมดอายุแล้ว
                      </Text>
                    }
                    color={color.BK}
                  >
                    <ActionBtn
                      onClick={() =>
                        row.viewStatus !== "EXPIRED" &&
                        navigate(`/newDiscountCo/coDiscountList/${row.coId}/list`)
                      }
                      icon={
                        <FormOutlined
                          style={{
                            color: row.viewStatus === "EXPIRED" ? color.Disable : color.primary,
                          }}
                        />
                      }
                    />
                  </Popover>
                </Permission>
              </Col>
              <Col>
                <Permission permission={["coDiscountList", "delete"]}>
                  <Popover
                    trigger={row.used > 0 ? "click" : undefined}
                    placement='bottom'
                    content={
                      <Text level={6} color='white'>
                        ไม่สามารถลบรายการส่วนลดที่มีการใช้งานแล้ว
                      </Text>
                    }
                    color={color.BK}
                  >
                    <ActionBtn
                      icon={
                        <DeleteOutlined
                          style={{ color: row.used > 0 ? color.Disable : color.error }}
                        />
                      }
                      onClick={() => !row.used && setDeleteModal({ id: row.coId, open: true })}
                    />
                  </Popover>
                </Permission>
              </Col>
            </Row>
          ),
        };
      },
    },
  ];

  return (
    <>
      <CardContainer>
        <PageTitle />
        <br />
        <Dashboard />
        <br />
        <Tabs
          style={{ fontFamily: "IBM Plex Sans Thai" }}
          activeKey={tab}
          onChange={(key) => {
            setPage("1");
            setTab(key);
            setParams({ tab: key, page: "1" });
          }}
          items={[
            {
              label: `ทั้งหมด (${data?.tabCounts?.all ?? 0})`,
              key: "ALL",
            },
            {
              label: `เปิดใช้งาน (${data?.tabCounts?.active ?? 0})`,
              key: "ACTIVE",
            },
            {
              label: `ปิดใช้งาน (${data?.tabCounts?.inactive ?? 0})`,
              key: "INACTIVE",
            },
            {
              label: `ใกล้หมดอายุ (${data?.tabCounts?.nearExpiry ?? 0})`,
              key: "NEAR_EXPIRY",
            },
            {
              label: `หมดอายุ (${data?.tabCounts?.expired ?? 0})`,
              key: "EXPIRED",
            },
          ]}
        />
        <Row justify={"space-between"} gutter={8}>
          <Col span={10}>
            <Input
              allowClear
              defaultValue={searchText}
              placeholder='ค้นหาส่วนลดดูแลราคา'
              suffix={<SearchOutlined />}
              onChange={(e) => {
                if (!e.target.value) {
                  setPage("1");
                  setSearchText(undefined);
                  setParams({ searchText: "", page: "1" });
                }
              }}
              onPressEnter={(e: any) => {
                if (e.target.value) {
                  setPage("1");
                  setSearchText(e.target.value);
                  setParams({ searchText: e.target.value, page: "1" });
                } else {
                  setPage("1");
                  setSearchText(undefined);
                  setParams({ searchText: "", page: "1" });
                }
              }}
            />
          </Col>
          <Col span={4}>
            <Select
              value={searchZone}
              data={
                zoneList.map((z) => ({ label: z.zoneName, value: z.zoneName, key: z.zoneName })) ||
                []
              }
              placeholder='เขต'
              onChange={(e) => {
                setPage("1");
                setSearchZone(e);
                setParams({ searchZone: e || "", page: "1" });
              }}
            />
          </Col>
          <Col span={5}>
            <Select
              value={searchType}
              data={
                coType?.map((type: any) => ({
                  label: type.nameTh,
                  value: type.creditMemoTypeId,
                  key: type.creditMemoTypeId,
                })) || []
              }
              placeholder='ประเภทส่วนลด'
              onChange={(e) => {
                setPage("1");
                setSearchType(e);
                setParams({ searchType: e || "", page: "1" });
              }}
            />
          </Col>
          <Col span={5}>
            <DatePicker.RangePicker
              format='DD/MM/YYYY'
              allowClear
              allowEmpty={[true, true]}
              // disabledDate={(current: any) => {
              //   return current && current.isAfter(dayjs().endOf("day"));
              // }}
              style={{ height: "40px" }}
              value={[
                searchDate[0] ? dayjs(searchDate[0]) : null,
                searchDate[1] ? dayjs(searchDate[1]) : null,
              ]}
              onChange={(dates) => {
                if (dates && dates[0] && dates[1]) {
                  const startDate = dayjs(dates[0])?.format("YYYY-MM-DD");
                  const endDate = dayjs(dates[1])?.format("YYYY-MM-DD");
                  setPage("1");
                  setSearchDate([startDate, endDate]);
                  setParams({ startDate, endDate, page: "1" });
                } else {
                  setPage("1");
                  setSearchDate([undefined, undefined]);
                  setParams({ startDate: "", endDate: "", page: "1" });
                }
              }}
            />
          </Col>
        </Row>
        <br />
        <ConfigProvider
          theme={{
            components: {
              Table: {
                headerBg: "#F3F6F9",
                fontFamily: "IBM Plex Sans Thai",
                fontSize: 15,
              },
            },
          }}
        >
          <Table
            loading={loading}
            columns={columns}
            dataSource={data?.data || []}
            tableLayout='fixed'
            scroll={{
              x: "max-content",
            }}
            pagination={{
              pageSize,
              current: Number(page),
              total: data?.count,
              showSizeChanger: false,
              position: ["bottomCenter"],
              onChange: (page) => {
                const nextPage = String(page);
                setPage(nextPage);
                setParams({ page: nextPage });
              },
            }}
            locale={{
              emptyText: () => (
                <div
                  style={{
                    height: "400px",
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  <Col>
                    <img src={image.emptyTableBrand} alt='empty_table' />
                  </Col>
                  <Col>
                    <div style={{ fontSize: "16px" }}>ไม่มีรายการส่วนลด</div>
                  </Col>
                </div>
              ),
            }}
          />
        </ConfigProvider>
      </CardContainer>
      {deleteModal?.open && (
        <Modal
          open={deleteModal.open}
          centered
          closable={false}
          onCancel={() => setDeleteModal({ id: "", open: false })}
          onOk={() => {
            deleteCoDiscoutListById(
              deleteModal.id,
              profile?.firstName + " " + profile?.lastName,
            ).then((res) => {
              messageApi.open({
                type: "error",
                content: "ลบรายการแล้ว",
                icon: <img src={icon.delete} style={{ width: 14, height: 16 }} />,
              });
              setDeleteModal({ id: "", open: false });
              fetchSummary();
              fetchData();
            });
          }}
          okText='ยืนยัน'
          cancelText='ยกเลิก'
          okButtonProps={{ danger: true }}
          cancelButtonProps={{ style: { color: color.error, borderColor: color.error } }}
        >
          <Text level={2}>ลบรายการส่วนลด</Text>
          <br />
          <Text color='Text3'>โปรดยืนยันการลบรายการส่วนลด</Text>
        </Modal>
      )}
      {isExporting && (
        <div
          style={{
            position: "fixed",
            inset: 0,
            backgroundColor: "rgba(128, 128, 128, 0.18)",
            cursor: "not-allowed",
            zIndex: 999,
          }}
        />
      )}
      {modalMutiSelect && (
        <Modal
          open={modalMutiSelect}
          footer={false}
          width={1000}
          onCancel={() => setModalMutiSelect(false)}
          centered
        >
          <Text level={4} fontWeight={600}>
            รายการที่ต้องการ Export จำนวน {coSelected.length} รายการ
          </Text>
          <Divider style={{ margin: "12px 0px 0px 12px", paddingBottom: "15px" }} />
          <Row justify='end' gutter={8} style={{ paddingBottom: "10px" }}>
            <Col>
              <Popconfirm
                placement='top'
                title={
                  <Row justify={"space-between"} gutter={8}>
                    <Col>
                      <Button
                        size='small'
                        title={
                          <Text level={6} color='primary'>
                            ยกเลิก
                          </Text>
                        }
                        typeButton='primary-light'
                        height={25}
                        style={{ padding: "0 12px" }}
                        onClick={() => resetSelected()}
                      />
                    </Col>
                    <Col>
                      <Button
                        size='small'
                        title={
                          <Text level={6} color='white'>
                            ลบ
                          </Text>
                        }
                        typeButton='danger'
                        height={25}
                        style={{ padding: "0 12px" }}
                        onClick={() => {
                          setModalMutiSelect(false);
                          resetSelected();
                        }}
                      />
                    </Col>
                  </Row>
                }
                icon={false}
                showCancel={false}
                okButtonProps={{ style: { display: "none" } }}
              >
                <Text level={6} color='primary' style={{ cursor: "pointer" }}>
                  ล้างทั้งหมด
                </Text>
              </Popconfirm>
            </Col>
          </Row>
          <Row
            justify='start'
            gutter={8}
            style={{
              padding: "8px",
              backgroundColor: color.Disable,
              borderTopLeftRadius: "10px",
              borderTopRightRadius: "10px",
            }}
          >
            <Col span={4}>
              <Text fontWeight={600}>รหัสส่วนลด</Text>
            </Col>
            <Col span={8}>
              <Text fontWeight={600}>ชื่อรายการ</Text>
            </Col>
            <Col span={6}>
              <Text fontWeight={600}>ประเภทส่วนลด</Text>
            </Col>
            <Col span={4}>
              <Text fontWeight={600}>วิธีคิดส่วนลด</Text>
            </Col>
            <Col span={2} style={{ textAlign: "right" }}>
              <Text fontWeight={600}>จัดการ</Text>
            </Col>
          </Row>
          <div style={{ maxHeight: 400, overflowY: "auto", paddingRight: 8, paddingTop: 8 }}>
            {coSelected.map((d: any, i: number) => (
              <Row key={i} justify='start' gutter={16} style={{ padding: "8px" }}>
                <Col span={4}>
                  <Text>{d.code}</Text>
                </Col>
                <Col span={8}>
                  <Text>{d.name}</Text>
                </Col>
                <Col span={6}>
                  <Tag style={{ backgroundColor: `${d?.creditMemoType?.color}20`, border: "none" }}>
                    <Typography.Text
                      style={{
                        color: d?.creditMemoType?.color,
                        fontFamily: "IBM Plex Sans Thai",
                        fontSize: 15,
                        fontWeight: "bold",
                      }}
                    >
                      {d?.creditMemoType?.code} • {d?.creditMemoType?.nameTh}
                    </Typography.Text>
                  </Tag>
                </Col>
                <Col span={4}>
                  <Text>{d.type === "SKU" ? "บาท (฿) / หน่วย" : "% ของยอดบิล"}</Text>
                </Col>
                <Col span={2} style={{ textAlign: "right" }}>
                  <Popconfirm
                    placement='top'
                    open={deleteConfirmId === d.coId}
                    onOpenChange={(open) => setDeleteConfirmId(open ? d.coId : null)}
                    title={
                      <Row justify={"space-between"} gutter={8}>
                        <Col>
                          <Button
                            size='small'
                            title={
                              <Text level={6} color='primary'>
                                ยกเลิก
                              </Text>
                            }
                            typeButton='primary-light'
                            height={25}
                            style={{ padding: "0 12px" }}
                            onClick={() => setDeleteConfirmId(null)}
                          />
                        </Col>
                        <Col>
                          <Button
                            size='small'
                            title={
                              <Text level={6} color='white'>
                                ลบ
                              </Text>
                            }
                            typeButton='danger'
                            height={25}
                            style={{ padding: "0 12px" }}
                            onClick={() => {
                              selectedOne(false, d.coId);
                              setDeleteConfirmId(null);
                              if (coSelected.length === 1) {
                                setModalMutiSelect(false);
                              }
                            }}
                          />
                        </Col>
                      </Row>
                    }
                    icon={false}
                    showCancel={false}
                    okButtonProps={{ style: { display: "none" } }}
                  >
                    <ActionBtn
                      icon={
                        <DeleteOutlined
                          style={{ color: color.error }}
                          //onClick={() => selectedOne(false, d.customerCompanyId)}
                        />
                      }
                    />
                  </Popconfirm>
                </Col>
              </Row>
            ))}
          </div>
          <Form form={form} layout='vertical'>
            <Row justify={"space-around"} gutter={8} style={{ padding: "5px" }}>
              <Col span={12}>
                <Form.Item
                  name='startDate'
                  label='วันที่เริ่มต้น'
                  rules={[
                    {
                      required: true,
                      message: "*โปรดเลือกวันที่เริ่มต้น",
                    },
                  ]}
                >
                  <DatePicker
                    style={{ height: "40px", width: "100%" }}
                    format='DD/MM/YYYY'
                    suffixIcon={<CalendarOutlined />}
                    disabledDate={(current) =>
                      current &&
                      (current.isBefore(dayjs().subtract(3, "year").startOf("year")) ||
                        current.isAfter(
                          endDateWatch ? dayjs(endDateWatch).endOf("day") : dayjs().endOf("day"),
                        ))
                    }
                  />
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item
                  name='endDate'
                  label='วันที่สิ้นสุด'
                  rules={[
                    {
                      required: true,
                      message: "*โปรดเลือกวันที่สิ้นสุด",
                    },
                  ]}
                >
                  <DatePicker
                    style={{ height: "40px", width: "100%" }}
                    format='DD/MM/YYYY'
                    suffixIcon={<CalendarOutlined />}
                    disabledDate={(current) =>
                      current &&
                      (current.isBefore(
                        startDateWatch
                          ? dayjs(startDateWatch).startOf("day")
                          : dayjs().subtract(3, "year").startOf("year"),
                      ) ||
                        current.isAfter(dayjs().endOf("day")))
                    }
                  />
                </Form.Item>
              </Col>
            </Row>
          </Form>
          <Alert
            message='หมายเหตุ สามารถเลือกช่วงวันที่ย้อนหลังได้ไม่เกิน 3 ปี'
            type='info'
            showIcon
          />
          <Divider />
          <Row gutter={16} justify={"end"}>
            <Col span={4}>
              <Button
                size='small'
                title='ยกเลิก'
                typeButton='primary-light'
                style={{
                  width: "100%",
                  height: "32px",
                  lineHeight: "32px",
                  padding: "0 16px",
                }}
                onClick={() => setModalMutiSelect(false)}
              />
            </Col>
            <Col span={4}>
              <Button
                size='small'
                title='Export'
                typeButton={"primary"}
                style={{
                  width: "100%",
                  height: "32px",
                  lineHeight: "32px",
                  padding: "0 16px",
                }}
                onClick={async () => {
                  exportMutiExcel();
                }}
              />
            </Col>
          </Row>
        </Modal>
      )}
    </>
  );
};

export const mapStatus = (status: string) => {
  const mapText = {
    ACTIVE: "เปิดใช้งาน",
    INACTIVE: "ปิดใช้งาน",
    NEAR_EXPIRY: "ใกล้หมดอายุ",
    EXPIRED: "หมดอายุ",
  };

  const mapTextColor = {
    ACTIVE: "#3A9757",
    INACTIVE: "#FFFFFF",
    NEAR_EXPIRY: "#7A6B0F",
    EXPIRED: "#8A93A8",
  };

  const mapBgColor = {
    ACTIVE: "#E2FDEB",
    INACTIVE: "#BABCBE",
    NEAR_EXPIRY: "#FFF7C9",
    EXPIRED: "#EFF2F9",
  };
  return { text: mapText[status], textColor: mapTextColor[status], bgColor: mapBgColor[status] };
};
